import React from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import { BlockMath } from 'react-katex';
import '../../../styles/Tutorial2.css';

const ChemicalReactionEngineeringNotes2 = () => {
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX:  'hidden',
          minHeight: 0,
        }}>
    <div className="cre2-container" style={{ background: 'var(--t2-bg)', minHeight: '100%' }}>

      {/* SIDEBAR */}
      <nav className="cre2-sidebar">
        <div className="cre2-sidebar-header">
          <h1>CRE Course</h1>
          <p>Prof. Fogler · Lecture 2</p>
        </div>

        <div className="cre2-nav-section">Review · Lecture 1</div>
        <a className="cre2-nav-item" href="#review-mole-balance">Reactor Mole Balances</a>
        <a className="cre2-nav-item" href="#review-cstr-example">CSTR Example Problem</a>

        <div className="cre2-nav-section">Chapter 2 · Conversion</div>
        <a className="cre2-nav-item" href="#define-conversion">Define Conversion, X</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#batch-conversion">↳ Batch Reactor</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#cstr-conversion">↳ CSTR</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#pfr-conversion">↳ PFR</a>
        <a className="cre2-nav-item" href="#mole-balance-summary-x">Summary in terms of X</a>

        <div className="cre2-nav-section">Reactor Sizing</div>
        <a className="cre2-nav-item" href="#levenspiel-plots">Levenspiel Plots</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#levenspiel-cstr">↳ CSTR Area</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#levenspiel-pfr">↳ PFR Area</a>
        <a className="cre2-nav-item" href="#numerical-eval">Numerical Evaluation</a>

        <div className="cre2-nav-section">Series Reactors</div>
        <a className="cre2-nav-item" href="#reactors-in-series">Reactors in Series</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#series-reactor1">↳ Reactor 1 (CSTR)</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#series-reactor2">↳ Reactor 2 (PFR)</a>
        <a className="cre2-nav-item cre2-nav-item--sub" href="#series-reactor3">↳ Reactor 3 (CSTR)</a>
        <a className="cre2-nav-item" href="#space-time">Space Time τ</a>

        <div className="cre2-nav-section">Reference</div>
        <a className="cre2-nav-item" href="#cre-algorithm">CRE Algorithm</a>
        <a className="cre2-nav-item" href="#key-symbols">Key Symbols</a>
      </nav>

      {/* MAIN CONTENT */}
      <main className="cre2-main">
        <div className="cre2-main-inner">

          {/* HERO */}
          <div className="cre2-hero">
            <div className="cre2-hero-tag">Open Course Notes</div>
            <h1>CRE — Lecture 2: Conversion &amp; Reactor Sizing</h1>
            <p className="cre2-hero-desc">
              Building on the General Mole Balance, this lecture introduces <strong>conversion X</strong> as the key design variable, rewrites all reactor design equations in terms of X, and develops <strong>Levenspiel Plots</strong> to graphically size CSTRs, PFRs, and series configurations.
            </p>
            <div className="cre2-hero-meta">
              <span className="cre2-meta-item"><span className="cre2-meta-dot"></span>Fogler's Elements of CRE, 6th Ed.</span>
              <span className="cre2-meta-item"><span className="cre2-meta-dot"></span>Lecture 2 · Chapter 2</span>
              <span className="cre2-meta-item"><span className="cre2-meta-dot"></span>University of Michigan</span>
            </div>
          </div>

          {/* TOC */}
          <div className="cre2-toc-box">
            <h3>Quick Navigation</h3>
            <div className="cre2-toc-grid">
              <a className="cre2-toc-link" href="#review-mole-balance">Reactor Mole Balances Review</a>
              <a className="cre2-toc-link" href="#define-conversion">Define Conversion, X</a>
              <a className="cre2-toc-link" href="#review-cstr-example">CSTR Example (Lec. 1)</a>
              <a className="cre2-toc-link" href="#batch-conversion">Batch in terms of X</a>
              <a className="cre2-toc-link" href="#mole-balance-summary-x">Summary Table (in X)</a>
              <a className="cre2-toc-link" href="#cstr-conversion">CSTR in terms of X</a>
              <a className="cre2-toc-link" href="#levenspiel-plots">Levenspiel Plots</a>
              <a className="cre2-toc-link" href="#pfr-conversion">PFR in terms of X</a>
              <a className="cre2-toc-link" href="#numerical-eval">Numerical Methods</a>
              <a className="cre2-toc-link" href="#reactors-in-series">Reactors in Series</a>
              <a className="cre2-toc-link" href="#space-time">Space Time τ</a>
              <a className="cre2-toc-link" href="#cre-algorithm">CRE Algorithm</a>
            </div>
          </div>

          {/* ══════════════════════════════════════════════
              REVIEW LECTURE 1
          ══════════════════════════════════════════════ */}
          <section className="cre2-section" id="review-mole-balance">
            <div className="cre2-lec-badge cre2-lec-badge--review">Review · Lecture 1</div>
            <div className="cre2-section-label">Review</div>
            <h2>Reactor Mole Balances Summary</h2>
            <div className="cre2-section-divider"></div>
            <p>
              The <strong>General Mole Balance Equation (GMBE)</strong> is applied to the four major reactor types for the general reaction A → B. Each reactor type yields a different form of the design equation depending on whether it is differential, algebraic, or integral.
            </p>

            <div style={{ margin: '24px 0', textAlign: 'center' }}>
              <img src="/lec2-mole-balance-summary.jpg" alt="Mole balance summary table for Batch, CSTR, PFR, and PBR reactors showing differential, algebraic, and integral forms" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <table className="cre2-table">
              <thead>
                <tr>
                  <th>Reactor</th>
                  <th>Differential Form</th>
                  <th>Algebraic Form</th>
                  <th>Integral Form</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="cre2-badge cre2-badge--batch">Batch</span></td>
                  <td><code className="cre2-inline-code">dN_A/dt = r_A · V</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">t = ∫ dN_A / (r_A·V)</code></td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--cstr">CSTR</span></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">V = (F_A0 − F_A) / (−r_A)</code></td>
                  <td>—</td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--pfr">PFR</span></td>
                  <td><code className="cre2-inline-code">dF_A/dV = r_A</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">V = ∫ dF_A / r_A</code></td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--pbr">PBR</span></td>
                  <td><code className="cre2-inline-code">dF_A/dW = r'_A</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">W = ∫ dF_A / r'_A</code></td>
                </tr>
              </tbody>
            </table>

            <div className="cre2-callout cre2-callout--info">
              <p><strong>Key Point:</strong> These mole balances are expressed in terms of molar flow rates (F_A) and moles (N_A). In Chapter 2, we rewrite all of these in terms of <em>conversion X</em> — a much more useful design variable.</p>
            </div>
          </section>

          {/* CSTR Example Review */}
          <section className="cre2-section" id="review-cstr-example">
            <div className="cre2-lec-badge cre2-lec-badge--review">Review · Lecture 1</div>
            <div className="cre2-section-label">Worked Example</div>
            <h2>CSTR Example Problem</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Given a liquid-phase CSTR with volumetric flow rate υ₀ = 10 dm³/min, inlet concentration C<sub>A0</sub>, and exit concentration C<sub>A</sub> = 0.1·C<sub>A0</sub>, find the reactor volume V.
            </p>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <img src="/lec2-cstr-example.jpg" alt="CSTR reactor diagram showing inlet and outlet conditions for example problem" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <div className="cre2-steps">
              <div className="cre2-step">
                <div className="cre2-step-num">1</div>
                <div className="cre2-step-content">
                  <h4>Mole Balance</h4>
                  <div className="cre2-eq-box">
                    <div className="cre2-eq-label">CSTR Design Equation</div>
                    <div className="cre2-eq-main">
                      <BlockMath math="V = \frac{F_{A0} - F_A}{-r_A} = \frac{\upsilon_0 [C_{A0} - C_A]}{-r_A}" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cre2-step">
                <div className="cre2-step-num">2</div>
                <div className="cre2-step-content">
                  <h4>Rate Law</h4>
                  <div className="cre2-eq-box">
                    <div className="cre2-eq-label">First-Order Rate Law</div>
                    <div className="cre2-eq-main"><BlockMath math="-r_A = k \cdot C_A" /></div>
                    <div className="cre2-eq-note">where k = 0.23 min⁻¹ (given)</div>
                  </div>
                </div>
              </div>

              <div className="cre2-step">
                <div className="cre2-step-num">3</div>
                <div className="cre2-step-content">
                  <h4>Stoichiometry (Liquid Phase: υ = υ₀)</h4>
                  <div className="cre2-eq-box">
                    <div className="cre2-eq-label">Concentration from Molar Flow Rate</div>
                    <div className="cre2-eq-main"><BlockMath math="C_A = \frac{F_A}{\upsilon} = \frac{F_A}{\upsilon_0}" /></div>
                  </div>
                </div>
              </div>

              <div className="cre2-step">
                <div className="cre2-step-num">4</div>
                <div className="cre2-step-content">
                  <h4>Combine</h4>
                  <div className="cre2-eq-box">
                    <div className="cre2-eq-label">Combined Design Equation</div>
                    <div className="cre2-eq-main"><BlockMath math="V = \frac{\upsilon_0 [C_{A0} - C_A]}{k \cdot C_A}" /></div>
                  </div>
                </div>
              </div>

              <div className="cre2-step">
                <div className="cre2-step-num">5</div>
                <div className="cre2-step-content">
                  <h4>Evaluate</h4>
                  <div className="cre2-eq-box">
                    <div className="cre2-eq-label">Numerical Solution</div>
                    <div className="cre2-eq-main">
                      <BlockMath math="C_A = 0.1 \cdot C_{A0}" />
                      <BlockMath math="V = \frac{10\ \text{dm}^3/\text{min} \cdot [C_{A0} - 0.1 C_{A0}]}{0.23\ \text{min}^{-1} \cdot 0.1 C_{A0}} = \frac{10 \times 0.9}{0.023}\ \text{dm}^3 \approx 391\ \text{dm}^3" />
                    </div>
                    <div className="cre2-eq-note">✔ V = 391 dm³</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              CHAPTER 2 — CONVERSION
          ══════════════════════════════════════════════ */}
          <section className="cre2-section" id="define-conversion">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Core Concept</div>
            <h2>Define Conversion, X</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Consider the generic reaction: <code className="cre2-inline-code">a A + b B → c C + d D</code>. We choose the <strong>limiting reactant A</strong> as our basis of calculation and normalize by dividing through by the stoichiometric coefficient <em>a</em>:
            </p>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <img src="/lec2-conversion-definition.jpg" alt="Conversion definition showing moles reacted over moles fed for reaction aA+bB→cC+dD" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Normalized Reaction</div>
              <div className="cre2-eq-main"><BlockMath math="A + \tfrac{b}{a}B \;\rightarrow\; \tfrac{c}{a}C + \tfrac{d}{a}D" /></div>
            </div>

            <div className="cre2-callout cre2-callout--key">
              <p><strong>Definition of Conversion X:</strong></p>
            </div>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Conversion</div>
              <div className="cre2-eq-main"><BlockMath math="X = \frac{\text{moles A reacted}}{\text{moles A fed}}" /></div>
              <div className="cre2-eq-note">X ranges from 0 (no reaction) to 1 (complete conversion of A). For irreversible reactions, X can approach 1. For reversible reactions, X is bounded by the equilibrium conversion X_e.</div>
            </div>

            <div className="cre2-card-grid">
              <div className="cre2-card">
                <div className="cre2-card-icon">🔄</div>
                <h4>Why Use X?</h4>
                <p>Conversion X is a dimensionless, normalized variable that directly measures reactor performance. All concentrations, molar flow rates, and volumes can be expressed in terms of X.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">🎯</div>
                <h4>Design Goal</h4>
                <p>Given a desired conversion X, find the reactor volume (CSTR/PFR) or time (Batch) required to achieve it — this is the central reactor design problem.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">⚠️</div>
                <h4>Limiting Reactant</h4>
                <p>X is always defined based on the limiting reactant A. This ensures X stays between 0 and 1, simplifying the mathematics.</p>
              </div>
            </div>
          </section>

          {/* BATCH in terms of X */}
          <section className="cre2-section" id="batch-conversion">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Batch Reactor</div>
            <h2>Batch Reactor Design Equation in Terms of X</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Starting from the moles of A remaining in the batch:
            </p>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <img src="/lec2-batch-conversion.jpg" alt="Batch reactor diagram showing design equation in terms of conversion X" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <div className="cre2-reactor-block cre2-reactor-block--batch">
              <div className="cre2-reactor-block-header">Batch — Moles of A Remaining</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Material Balance on A</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="N_A = N_{A0} - N_{A0} X" />
                    <BlockMath math="dN_A = -N_{A0}\, dX" />
                  </div>
                  <div className="cre2-eq-note">Moles A remaining = Moles A initially − Moles A reacted = N_A0 − N_A0·X</div>
                </div>
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Differential Form (substituting into GMBE)</div>
                  <div className="cre2-eq-main"><BlockMath math="N_{A0}\,\frac{dX}{dt} = -r_A \cdot V" /></div>
                </div>
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Integral Form (time to achieve conversion X)</div>
                  <div className="cre2-eq-main"><BlockMath math="t = N_{A0} \int_0^X \frac{dX}{-r_A \cdot V}" /></div>
                  <div className="cre2-eq-note">This gives the time t needed to reach conversion X starting from X = 0 at t = 0.</div>
                </div>
              </div>
            </div>
          </section>

          {/* CSTR in terms of X */}
          <section className="cre2-section" id="cstr-conversion">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">CSTR</div>
            <h2>CSTR Design Equation in Terms of X</h2>
            <div className="cre2-section-divider"></div>
            <p>
              At <strong>steady state</strong> (dN_A/dt = 0) and <strong>well mixed</strong>, the mole balance on A gives:
            </p>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <img src="/lec2-cstr-conversion.jpg" alt="CSTR reactor diagram showing design equation V = F_A0*X/(-r_A) in terms of conversion" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <div className="cre2-reactor-block cre2-reactor-block--cstr">
              <div className="cre2-reactor-block-header">CSTR — Molar Flow Rate of A</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Conversion-Flow Rate Relationship</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="F_A = F_{A0} - F_{A0} X" />
                  </div>
                </div>
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">CSTR Volume (Algebraic — Evaluated at Exit Conditions)</div>
                  <div className="cre2-eq-main"><BlockMath math="V = \frac{F_{A0} \cdot X}{-r_A}" /></div>
                  <div className="cre2-eq-note">
                    Note: −r_A is evaluated at the <em>exit</em> conversion X, since the CSTR is well-mixed (uniform conditions = exit conditions). This is why CSTR requires more volume than PFR for the same conversion.
                  </div>
                </div>
              </div>
            </div>

            <div className="cre2-callout cre2-callout--warn">
              <p><strong>Important:</strong> The CSTR design equation is <em>algebraic</em>, not an integral. The rate −r_A is evaluated at the single exit conversion X — NOT integrated over a range of conversions as in the PFR.</p>
            </div>
          </section>

          {/* PFR in terms of X */}
          <section className="cre2-section" id="pfr-conversion">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">PFR</div>
            <h2>PFR Design Equation in Terms of X</h2>
            <div className="cre2-section-divider"></div>
            <p>
              In a PFR, composition changes along the length of the reactor. Starting from dF_A/dV = r_A at steady state:
            </p>

            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <img src="/lec2-pfr-conversion.jpg" alt="PFR reactor diagram showing design equation F_A0*dX/dV = -r_A and integral form in terms of conversion" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <div className="cre2-reactor-block cre2-reactor-block--pfr">
              <div className="cre2-reactor-block-header">PFR — Differential and Integral Forms</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Differential Form</div>
                  <div className="cre2-eq-main"><BlockMath math="F_{A0}\,\frac{dX}{dV} = -r_A" /></div>
                  <div className="cre2-eq-note">Derived using F_A = F_A0 − F_A0·X, so dF_A = −F_A0·dX</div>
                </div>
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Integral Form (PFR volume to achieve conversion X)</div>
                  <div className="cre2-eq-main"><BlockMath math="V = F_{A0} \int_0^X \frac{dX}{-r_A}" /></div>
                  <div className="cre2-eq-note">Boundary conditions: V = 0 when X = 0; V = V when X = X</div>
                </div>
              </div>
            </div>

            <div className="cre2-callout cre2-callout--tip">
              <p><strong>PFR vs CSTR:</strong> The PFR integrates 1/(−r_A) from 0 to X (area under the curve), while the CSTR uses a rectangle of height 1/(−r_A)|_X and width X. Since 1/(−r_A) increases with X for normal kinetics, the CSTR rectangle is always larger — PFR needs less volume!</p>
            </div>
          </section>

          {/* MOLE BALANCE SUMMARY IN TERMS OF X */}
          <section className="cre2-section" id="mole-balance-summary-x">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Summary</div>
            <h2>Reactor Mole Balances in Terms of Conversion X</h2>
            <div className="cre2-section-divider"></div>
            <p>All reactor design equations rewritten using conversion X as the primary variable:</p>

            <div style={{ margin: '24px 0', textAlign: 'center' }}>
              <img src="/lec2-mole-balance-x-summary.jpg" alt="Comprehensive mole balance summary table for all reactors in terms of conversion X" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
            </div>

            <table className="cre2-table">
              <thead>
                <tr>
                  <th>Reactor</th>
                  <th>Differential Form</th>
                  <th>Algebraic Form</th>
                  <th>Integral Form</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="cre2-badge cre2-badge--batch">Batch</span></td>
                  <td><code className="cre2-inline-code">N_A0·dX/dt = −r_A·V</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">t = N_A0·∫dX/(−r_A·V)</code></td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--cstr">CSTR</span></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">V = F_A0·X / (−r_A)</code></td>
                  <td>—</td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--pfr">PFR</span></td>
                  <td><code className="cre2-inline-code">F_A0·dX/dV = −r_A</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">V = F_A0·∫dX/(−r_A)</code></td>
                </tr>
                <tr>
                  <td><span className="cre2-badge cre2-badge--pbr">PBR</span></td>
                  <td><code className="cre2-inline-code">F_A0·dX/dW = −r'_A</code></td>
                  <td>—</td>
                  <td><code className="cre2-inline-code">W = F_A0·∫dX/(−r'_A)</code></td>
                </tr>
              </tbody>
            </table>

            <div className="cre2-callout cre2-callout--key">
              <p><strong>Common Thread:</strong> All reactor design equations have the same structure — a ratio of <em>how much conversion is needed</em> to <em>how fast the reaction occurs</em>. The reactor volume (or time) is fundamentally governed by this ratio.</p>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              REACTOR SIZING — LEVENSPIEL PLOTS
          ══════════════════════════════════════════════ */}
          <section className="cre2-section" id="levenspiel-plots">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Reactor Sizing</div>
            <h2>Levenspiel Plots</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Given −r_A as a function of conversion X (i.e., <strong>−r_A = f(X)</strong>), we can size any reactor type. This is done by constructing a <strong>Levenspiel plot</strong> — a graph of F_A0/(−r_A) vs X.
            </p>

            <div style={{ margin: '24px 0', textAlign: 'center' }}>
              <img src="/lec2-levenspiel-comparison.jpg" alt="Levenspiel plot comparison showing CSTR rectangle and PFR area under the curve" style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid var(--t2-border)' }} />
              <p style={{ fontSize: '12px', color: 'var(--t2-text-sub)', marginTop: '8px' }}>CSTR requires rectangular area; PFR requires area under curve (smaller volume for same conversion)</p>
            </div>

            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Levenspiel Plot Ordinate</div>
              <div className="cre2-eq-main"><BlockMath math="\frac{F_{A0}}{-r_A} = g(X)" /></div>
              <div className="cre2-eq-note">
                This function g(X) has units of volume (dm³ or m³). For normal reactions, as X → 1, the rate −r_A → 0 and g(X) → ∞, making the curve rise steeply near complete conversion.
              </div>
            </div>

            <div className="cre2-card-grid">
              <div className="cre2-card">
                <div className="cre2-card-icon">📊</div>
                <h4>What the Plot Shows</h4>
                <p>The y-axis is F_A0/(−r_A) in units of volume. The x-axis is conversion X from 0 to 1. The curve typically rises steeply near equilibrium conversion X_e.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">▭</div>
                <h4>CSTR = Rectangle</h4>
                <p>CSTR volume = rectangle area with width X₁ and height g(X₁). Evaluated at the <em>exit</em> conversion — always an overestimate of the area under the curve.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">📐</div>
                <h4>PFR = Area Under Curve</h4>
                <p>PFR volume = area under the Levenspiel curve from 0 to X₁. This is the integral ∫g(X)dX, always less than the CSTR rectangle for normal kinetics.</p>
              </div>
            </div>

            {/* Visual representation of Levenspiel plot */}
            <div className="cre2-levenspiel-container">
              <div className="cre2-levenspiel-title">Levenspiel Plot — F_A0/(−r_A) vs Conversion X</div>
              <svg viewBox="0 0 520 300" className="cre2-levenspiel-svg" aria-label="Levenspiel plot showing CSTR and PFR areas">
                {/* Grid lines */}
                {[0.2,0.4,0.6,0.8].map((x, i) => (
                  <line key={i} x1={60 + x*380} y1={20} x2={60 + x*380} y2={260} stroke="#e5dbd0" strokeWidth="1" strokeDasharray="3,3"/>
                ))}
                {[50,100,150,200].map((y, i) => (
                  <line key={i} x1={60} y1={260-y} x2={440} y2={260-y} stroke="#e5dbd0" strokeWidth="1" strokeDasharray="3,3"/>
                ))}
                {/* Axes */}
                <line x1="60" y1="260" x2="450" y2="260" stroke="#1a1209" strokeWidth="2"/>
                <line x1="60" y1="260" x2="60" y2="15" stroke="#1a1209" strokeWidth="2"/>
                {/* Axis arrows */}
                <polygon points="450,257 450,263 458,260" fill="#1a1209"/>
                <polygon points="57,15 63,15 60,7" fill="#1a1209"/>
                {/* Axis labels */}
                <text x="454" y="264" fontSize="12" fill="#1a1209" fontFamily="DM Mono, monospace">X</text>
                <text p="10" q="20" fontSize="10" fill="#1a1209" fontFamily="DM Mono, monospace" transform="rotate(-90,20,140)" textAnchor="middle" dominantBaseline="middle" x="20" y="140">F_A0/(-r_A)</text>
                {/* X axis ticks */}
                {[0,0.2,0.4,0.6,0.8].map((x, i) => (
                  <g key={i}>
                    <line x1={60+x*380} y1="260" x2={60+x*380} y2="266" stroke="#1a1209" strokeWidth="1.5"/>
                    <text x={60+x*380} y="278" fontSize="10" fill="#4a3f38" textAnchor="middle" fontFamily="DM Mono, monospace">{x}</text>
                  </g>
                ))}
                {/* CSTR shaded rectangle (pink) up to X1=0.8 */}
                <rect x="60" y={260 - 228} width={380*0.8} height="228" fill="rgba(224,96,58,0.18)" stroke="rgba(224,96,58,0.4)" strokeWidth="1.2"/>
                {/* PFR shaded area (purple) under curve */}
                <path
                  d={`M 60,${260-40} C 120,${260-42} 160,${260-48} 200,${260-58} C 250,${260-72} 290,${260-96} 330,${260-132} C 360,${260-162} 380,${260-190} 364,${260-228} L 364,260 L 60,260 Z`}
                  fill="rgba(74,144,217,0.22)" stroke="rgba(74,144,217,0.5)" strokeWidth="1.2"
                />
                {/* The curve */}
                <path
                  d={`M 60,${260-40} C 120,${260-42} 160,${260-48} 200,${260-58} C 250,${260-72} 290,${260-96} 330,${260-132} C 360,${260-162} 380,${260-192} 395,${260-230}`}
                  fill="none" stroke="#1a1209" strokeWidth="2.5"
                />
                {/* X1 vertical line */}
                <line x1={60+0.8*380} y1="260" x2={60+0.8*380} y2={260-228} stroke="#1a1209" strokeWidth="1.5" strokeDasharray="4,3"/>
                {/* Labels */}
                <text x={60+0.8*380+6} y={260-220} fontSize="11" fill="#1a1209" fontFamily="DM Mono, monospace">X₁</text>
                <text x="200" y={260-200} fontSize="11" fill="rgba(224,96,58,0.9)" fontFamily="DM Sans, sans-serif" fontWeight="600">CSTR</text>
                <text x="200" y={260-186} fontSize="10" fill="rgba(224,96,58,0.8)" fontFamily="DM Sans, sans-serif">(rectangle)</text>
                <text x="95" y={260-60} fontSize="11" fill="rgba(74,144,217,0.9)" fontFamily="DM Sans, sans-serif" fontWeight="600">PFR</text>
                <text x="95" y={260-46} fontSize="10" fill="rgba(74,144,217,0.8)" fontFamily="DM Sans, sans-serif">(area under curve)</text>
                {/* Y axis label */}
                <text x="14" y="140" fontSize="10" fill="#4a3f38" fontFamily="DM Mono, monospace" transform="rotate(-90,18,140)" textAnchor="middle">F_A0 / (−r_A)</text>
              </svg>
              <div className="cre2-levenspiel-caption">
                For the same conversion X₁, V_CSTR (rectangle) &gt; V_PFR (area under curve). The PFR is always more efficient for normal kinetics where −r_A decreases with increasing X.
              </div>
            </div>
          </section>

          {/* CSTR on Levenspiel */}
          <section className="cre2-section" id="levenspiel-cstr">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Reactor Sizing — CSTR</div>
            <h2>CSTR Volume from Levenspiel Plot</h2>
            <div className="cre2-section-divider"></div>
            <p>The CSTR volume is represented as a <strong>rectangle</strong> on the Levenspiel plot:</p>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">CSTR Volume = Rectangle Area</div>
              <div className="cre2-eq-main">
                <BlockMath math="V_{\text{CSTR}} = \left[\frac{F_{A0}}{-r_A}\right]_{X_1} \cdot X_1" />
              </div>
              <div className="cre2-eq-note">
                Height = F_A0/(−r_A) evaluated at exit conversion X₁ &nbsp;|&nbsp; Width = X₁{'\n'}
                The rectangle overshoots the curve because the CSTR operates entirely at the lowest possible rate (exit conditions).
              </div>
            </div>
          </section>

          {/* PFR on Levenspiel */}
          <section className="cre2-section" id="levenspiel-pfr">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Reactor Sizing — PFR</div>
            <h2>PFR Volume from Levenspiel Plot</h2>
            <div className="cre2-section-divider"></div>
            <p>The PFR volume is represented as the <strong>area under the curve</strong> on the Levenspiel plot:</p>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">PFR Volume = Area Under Curve</div>
              <div className="cre2-eq-main">
                <BlockMath math="V_{\text{PFR}} = \int_0^{X_1} \frac{F_{A0}}{-r_A}\, dX" />
              </div>
              <div className="cre2-eq-note">
                This integral is evaluated numerically (from tabulated data) or analytically (if −r_A = f(X) is known explicitly).
              </div>
            </div>

            <div className="cre2-callout cre2-callout--tip">
              <p><strong>Reading the Levenspiel Plot Example (from lecture data):</strong> At X = 0.8 with the given kinetics, V_CSTR ≈ 28 × 0.8 = 22.4 dm³ and V_PFR = ∫₀^0.8 g(X)dX ≈ the smaller shaded area under the curve — always less than V_CSTR.</p>
            </div>
          </section>

          {/* NUMERICAL EVALUATION */}
          <section className="cre2-section" id="numerical-eval">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Numerical Methods</div>
            <h2>Numerical Evaluation of Integrals</h2>
            <div className="cre2-section-divider"></div>
            <p>
              When −r_A is given as <em>tabulated data</em> (not an explicit function), the PFR volume integral must be evaluated numerically. The most commonly used methods are:
            </p>

            <div className="cre2-card-grid">
              <div className="cre2-card">
                <div className="cre2-card-icon">📏</div>
                <h4>Trapezoidal Rule</h4>
                <p>Uses two data points. Simple but less accurate. Good when data is scarce.</p>
                <div className="cre2-eq-box" style={{marginTop: '10px'}}>
                  <div className="cre2-eq-main" style={{fontSize: '12px'}}><BlockMath math="\int \approx \tfrac{\Delta x}{2}\left[g(0) + g(X)\right]" /></div>
                </div>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">📐</div>
                <h4>Simpson's 1/3 Rule</h4>
                <p>Uses three data points (two intervals). Most commonly used. High accuracy with smooth functions.</p>
                <div className="cre2-eq-box" style={{marginTop: '10px'}}>
                  <div className="cre2-eq-main" style={{fontSize: '12px'}}><BlockMath math="\int \approx \tfrac{\Delta x}{3}\left[g(0) + 4g\!\left(\tfrac{X}{2}\right) + g(X)\right]" /></div>
                </div>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">🔢</div>
                <h4>Simpson's 3/8 Rule</h4>
                <p>Uses four data points. More accurate than 1/3 Rule when additional data is available.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">⚡</div>
                <h4>Five-Point Quadrature</h4>
                <p>Uses five data points. Highest accuracy for smooth curves; preferred when precise results are needed.</p>
              </div>
            </div>

            <div className="cre2-example-box">
              <div className="cre2-example-tag">Simpson's 1/3 Rule — Full Expression</div>
              <h4>PFR Volume by Simpson's One-Third Rule</h4>
              <div className="cre2-eq-box">
                <div className="cre2-eq-label">Simpson's 1/3 Rule</div>
                <div className="cre2-eq-main">
                  <BlockMath math="V = \int_0^X \frac{F_{A0}}{-r_A}\,dX \approx \frac{\Delta x}{3} \cdot F_{A0}\left[\frac{1}{-r_A(0)} + \frac{4}{-r_A(X/2)} + \frac{1}{-r_A(X)}\right]" />
                </div>
                <div className="cre2-eq-note">
                  where Δx = X (full interval width). The factor 4 is applied to the midpoint value — this is what gives Simpson's rule its superior accuracy compared to the Trapezoidal rule.
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              REACTORS IN SERIES
          ══════════════════════════════════════════════ */}
          <section className="cre2-section" id="reactors-in-series">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Multiple Reactors</div>
            <h2>Reactors in Series</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Given −r_A as a function of conversion, we can design any <strong>sequence of reactors in series</strong> by defining a <em>cumulative conversion X_i</em>:
            </p>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Cumulative Conversion at Point i</div>
              <div className="cre2-eq-main">
                <BlockMath math="X_i = \frac{\text{total moles of A reacted up to point } i}{\text{moles of A fed to first reactor}}" />
              </div>
              <div className="cre2-eq-note">⚠️ Only valid if there are NO side streams between reactors.</div>
            </div>

            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Molar Flow Rate at Point i</div>
              <div className="cre2-eq-main"><BlockMath math="F_{Ai} = F_{A0} - F_{A0} X_i" /></div>
            </div>

            {/* Series diagram */}
            <div className="cre2-series-diagram">
              <div className="cre2-series-reactor cre2-series-reactor--cstr">
                <div className="cre2-series-label">CSTR</div>
                <div className="cre2-series-vol">V₁</div>
                <div className="cre2-series-rate">r_A1</div>
              </div>
              <div className="cre2-series-arrow">
                <span>X₁ = 0.2</span>
                <div className="cre2-arrow-line">→</div>
              </div>
              <div className="cre2-series-reactor cre2-series-reactor--pfr">
                <div className="cre2-series-label">PFR</div>
                <div className="cre2-series-vol">V₂</div>
              </div>
              <div className="cre2-series-arrow">
                <span>X₂ = 0.6</span>
                <div className="cre2-arrow-line">→</div>
              </div>
              <div className="cre2-series-reactor cre2-series-reactor--cstr">
                <div className="cre2-series-label">CSTR</div>
                <div className="cre2-series-vol">V₃</div>
                <div className="cre2-series-rate">r_A3</div>
              </div>
              <div className="cre2-series-arrow">
                <span>X₃ = 0.65</span>
                <div className="cre2-arrow-line">→</div>
              </div>
            </div>
          </section>

          {/* Reactor 1 in Series */}
          <section className="cre2-section" id="series-reactor1">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Series — Reactor 1</div>
            <h2>Reactor 1: First CSTR</h2>
            <div className="cre2-section-divider"></div>
            <p>The first CSTR takes the feed from X = 0 to X₁ = 0.2:</p>
            <div className="cre2-reactor-block cre2-reactor-block--cstr">
              <div className="cre2-reactor-block-header">CSTR — Reactor 1</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Volume of First CSTR</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="F_{A1} = F_{A0} - F_{A0} X_1" />
                    <BlockMath math="V_1 = \frac{F_{A0} - F_{A1}}{-r_{A1}} = \frac{F_{A0} X_1}{-r_{A1}}" />
                  </div>
                  <div className="cre2-eq-note">
                    On the Levenspiel plot: V₁ = rectangle with width X₁ and height g(X₁). Evaluated at X₁ = 0.2.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reactor 2 in Series */}
          <section className="cre2-section" id="series-reactor2">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Series — Reactor 2</div>
            <h2>Reactor 2: PFR</h2>
            <div className="cre2-section-divider"></div>
            <p>The PFR takes the stream from X₁ = 0.2 to X₂ = 0.6:</p>
            <div className="cre2-reactor-block cre2-reactor-block--pfr">
              <div className="cre2-reactor-block-header">PFR — Reactor 2</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Volume of PFR</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="V_2 = \int_{X_1}^{X_2} \frac{F_{A0}}{-r_A}\,dX" />
                  </div>
                  <div className="cre2-eq-note">
                    On the Levenspiel plot: V₂ = area under the curve between X₁ and X₂. The PFR benefits from operating through the minimum of the Levenspiel curve (where −r_A is fastest).
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reactor 3 in Series */}
          <section className="cre2-section" id="series-reactor3">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Series — Reactor 3</div>
            <h2>Reactor 3: Second CSTR</h2>
            <div className="cre2-section-divider"></div>
            <p>The second CSTR takes the stream from X₂ = 0.6 to X₃ = 0.65:</p>
            <div className="cre2-reactor-block cre2-reactor-block--cstr">
              <div className="cre2-reactor-block-header">CSTR — Reactor 3</div>
              <div className="cre2-reactor-block-body">
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Mole Balance on Reactor 3</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="F_{A2} - F_{A3} + r_{A3} V_3 = 0" />
                    <BlockMath math="(F_{A0} - F_{A0}X_2) - (F_{A0} - F_{A0}X_3) + r_{A3}V_3 = 0" />
                  </div>
                </div>
                <div className="cre2-eq-box">
                  <div className="cre2-eq-label">Volume of Second CSTR</div>
                  <div className="cre2-eq-main">
                    <BlockMath math="V_3 = \frac{F_{A0}(X_3 - X_2)}{-r_{A3}}" />
                  </div>
                  <div className="cre2-eq-note">
                    On the Levenspiel plot: V₃ = rectangle with width (X₃ − X₂) and height g(X₃). This rectangle is tall because −r_A is very small near X₂ = 0.6 → X_e.
                  </div>
                </div>
              </div>
            </div>

            <div className="cre2-callout cre2-callout--info">
              <p><strong>Series Configuration from Lecture Data:</strong> For the given kinetics (autocatalytic-like curve with a minimum in g(X)), the optimal series arrangement CSTR→PFR→CSTR gives: V₁ ≈ 188 dm³, V₂ ≈ 380 dm³, V₃ ≈ 100 dm³.</p>
            </div>
          </section>

          {/* SPACE TIME */}
          <section className="cre2-section" id="space-time">
            <div className="cre2-lec-badge cre2-lec-badge--lec2">Lecture 2 · Chapter 2</div>
            <div className="cre2-section-label">Design Parameter</div>
            <h2>Space Time τ</h2>
            <div className="cre2-section-divider"></div>
            <p>
              Space time is a key parameter relating reactor size to throughput. It represents how long it would take to process one reactor volume of fluid at inlet conditions:
            </p>
            <div className="cre2-eq-box">
              <div className="cre2-eq-label">Space Time Definition</div>
              <div className="cre2-eq-main"><BlockMath math="\tau = \frac{V}{\upsilon_0}" /></div>
              <div className="cre2-eq-note">
                τ = Space time [units: time, e.g. min or s]{'\n'}
                V = Reactor volume [dm³]{'\n'}
                υ₀ = Volumetric flow rate at inlet conditions [dm³/min]{'\n\n'}
                Space time is also called the <em>residence time</em> in some contexts (though strictly they differ for gas-phase reactions).
              </div>
            </div>

            <div className="cre2-card-grid">
              <div className="cre2-card">
                <div className="cre2-card-icon">⏱️</div>
                <h4>Physical Meaning</h4>
                <p>τ is the time it would take the inlet feed (at υ₀) to fill the entire reactor volume V. It normalizes reactor size by throughput.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">📐</div>
                <h4>Reactor Scale-Up</h4>
                <p>For the same reaction at the same conditions, maintaining constant τ ensures the same conversion X regardless of reactor size — the basis for scale-up design.</p>
              </div>
              <div className="cre2-card">
                <div className="cre2-card-icon">🔄</div>
                <h4>Typical Values</h4>
                <p>Batch: hours to days. CSTR: minutes to hours. PFR: seconds to minutes. PBR: depends on reaction and catalyst.</p>
              </div>
            </div>

            <div className="cre2-example-box">
              <div className="cre2-example-tag">Space Time Example</div>
              <h4>Two-Section PFR</h4>
              <p>
                A PFR consists of two 20 m sections, each with V = 0.2 m³ and total V = 0.4 m³. If υ₀ = 0.01 m³/s:
              </p>
              <div className="cre2-eq-box">
                <div className="cre2-eq-label">Space Time Calculation</div>
                <div className="cre2-eq-main">
                  <BlockMath math="\tau = \frac{V}{\upsilon_0} = \frac{0.4\ \text{m}^3}{0.01\ \text{m}^3/\text{s}} = 40\ \text{s}" />
                </div>
                <div className="cre2-eq-note">The reactor processes one full volume of feed every 40 seconds.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              CRE ALGORITHM
          ══════════════════════════════════════════════ */}
          <section className="cre2-section" id="cre-algorithm">
            <div className="cre2-section-label">Reference</div>
            <h2>The CRE Algorithm — Tower of CRE</h2>
            <div className="cre2-section-divider"></div>
            <p>
              All CRE problems are solved by stacking these building blocks in order. Each block depends on the previous one — cutting corners at any level destabilizes the whole structure.
            </p>

            <div className="cre2-tower">
              <div className="cre2-tower-level cre2-tower-level--1">
                <div className="cre2-tower-num">1</div>
                <div className="cre2-tower-content">
                  <h4>Mole Balance</h4>
                  <p>Write the design equation for your reactor type. Express in terms of conversion X.</p>
                </div>
              </div>
              <div className="cre2-tower-level cre2-tower-level--2">
                <div className="cre2-tower-num">2</div>
                <div className="cre2-tower-content">
                  <h4>Rate Laws</h4>
                  <p>Obtain −r_A as a function of concentrations from experiment or theory.</p>
                </div>
              </div>
              <div className="cre2-tower-level cre2-tower-level--3">
                <div className="cre2-tower-num">3</div>
                <div className="cre2-tower-content">
                  <h4>Stoichiometry</h4>
                  <p>Express all concentrations in terms of X. Account for phase (gas/liquid) and expansion (ε).</p>
                </div>
              </div>
              <div className="cre2-tower-level cre2-tower-level--4">
                <div className="cre2-tower-num">4</div>
                <div className="cre2-tower-content">
                  <h4>Isothermal Design</h4>
                  <p>Combine blocks 1–3 and solve for V, W, or t. Use Levenspiel plots or ODE solvers as needed.</p>
                </div>
              </div>
              <div className="cre2-tower-level cre2-tower-level--5">
                <div className="cre2-tower-num">5</div>
                <div className="cre2-tower-content">
                  <h4>Heat Effects</h4>
                  <p>Add energy balance for non-isothermal systems. Temperature couples with conversion — solve simultaneously.</p>
                </div>
              </div>
            </div>

            <div className="cre2-callout cre2-callout--warn">
              <p><strong>Warning:</strong> Be careful not to cut corners on <em>any</em> of the CRE building blocks while learning this material. If you skip stoichiometry, the algorithm becomes unstable and your reactor designs will be incorrect.</p>
            </div>
          </section>

          {/* KEY SYMBOLS */}
          <section className="cre2-section" id="key-symbols">
            <div className="cre2-section-label">Reference</div>
            <h2>Key Symbols &amp; Variables — Lecture 2</h2>
            <div className="cre2-section-divider"></div>

            <table className="cre2-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Meaning</th>
                  <th>Typical Units</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>X</td><td>Conversion of limiting reactant A (moles reacted / moles fed)</td><td>dimensionless (0 to 1)</td></tr>
                <tr><td>X_e</td><td>Equilibrium conversion (maximum achievable for reversible reaction)</td><td>dimensionless</td></tr>
                <tr><td>N_A0</td><td>Initial moles of A in batch reactor</td><td>mol</td></tr>
                <tr><td>N_A</td><td>Moles of A remaining at time t</td><td>mol</td></tr>
                <tr><td>F_A0</td><td>Inlet molar flow rate of A</td><td>mol/s or mol/min</td></tr>
                <tr><td>F_A</td><td>Molar flow rate of A at conversion X</td><td>mol/s or mol/min</td></tr>
                <tr><td>−r_A</td><td>Rate of disappearance of A per unit volume</td><td>mol/dm³/s</td></tr>
                <tr><td>−r'_A</td><td>Rate per unit mass of catalyst (PBR)</td><td>mol/kg_cat/s</td></tr>
                <tr><td>g(X)</td><td>Levenspiel plot function = F_A0/(−r_A)</td><td>dm³ or m³</td></tr>
                <tr><td>τ</td><td>Space time = V/υ₀</td><td>s, min</td></tr>
                <tr><td>υ₀</td><td>Volumetric flow rate at inlet</td><td>dm³/min or m³/s</td></tr>
                <tr><td>V</td><td>Reactor volume</td><td>dm³ or m³</td></tr>
                <tr><td>t</td><td>Time in batch reactor to reach conversion X</td><td>s or min</td></tr>
                <tr><td>W</td><td>Catalyst weight in PBR</td><td>kg</td></tr>
                <tr><td>Δx</td><td>Integration step width in numerical methods</td><td>dimensionless</td></tr>
              </tbody>
            </table>
          </section>

          <div className="cre2-footer">
            Based on: H. Scott Fogler, "Elements of Chemical Reaction Engineering," 6th Edition, University of Michigan · Lecture 2 · Chapter 2 · Course notes compiled for educational use
          </div>

        </div>
      </main>
    </div>

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default ChemicalReactionEngineeringNotes2;